// Cross-subdomain return-host cookie for Discourse SSO. The MH client routes
// all Settings → Community clicks to the single canonical Discourse at
// `community.{rootDomain}`, which always sends users back to its configured
// `sso_url` (= `https://www.{rootDomain}/`). When the originating client lives
// on a different subdomain (e.g. `dev.*`, `canary.*`), the cookie set by
// `setReturnHostCookie` lets the SSO entry path on `www` know to bounce the
// user back to that originating host before running the SSO exchange.
//
// Mirrors the PPG `ppg_return_host` cookie pattern from
// client/src/services/external-flow.service.ts (the only other place in the
// codebase that needs this same shape of cross-origin return). Lifted into a
// pair of pure functions here so both call sites stay tiny and the bounce
// decision is unit-testable. See design-docs/discourse-sso.md for the full
// flow diagram.
//
// The module also owns `parseSsoReturn` below, which is not about the cookie: it
// reads the sign-in pair out of a return address. It lives here because this is the
// Discourse module with no Angular in it, which is what lets both the web page-load
// path and the Angular-free deep-link flow share one parser (#4688).

const COOKIE_NAME = 'discourse_return_host';
const MAX_AGE_SEC = 300; // 5 min — the round-trip is seconds; 5 min is roomy slack
const SET_FLAGS = 'secure;samesite=lax';

export type ReturnHostResult =
  | { kind: 'absent' }
  | { kind: 'matches' }
  | { kind: 'mismatch'; host: string };

// drops the return-host cookie before bouncing to community.{rootDomain}.
// scope is the full root domain so the cookie is visible to www on the SSO
// return leg.
export function setReturnHostCookie(currentHost: string, rootDomain: string): void {
  document.cookie = `${COOKIE_NAME}=${currentHost};domain=.${rootDomain};path=/;max-age=${MAX_AGE_SEC};${SET_FLAGS}`;
}

// reads the cookie, clears it, and reports what the caller should do.
// always clears: a stale cookie that survived a prior bounce should not
// re-bounce a user who navigated here directly with sso/sig params.
export function consumeReturnHostCookie(currentHost: string, rootDomain: string): ReturnHostResult {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${COOKIE_NAME}=([^;]+)`));
  if (!match) {
    return { kind: 'absent' };
  }
  document.cookie = `${COOKIE_NAME}=;domain=.${rootDomain};path=/;max-age=0;${SET_FLAGS}`;
  const host = match[1];
  if (host === currentHost) {
    return { kind: 'matches' };
  }
  return { kind: 'mismatch', host };
}

/**
 * The pair of query values the forum hands back, which together are a sign-in
 * request. Both are still percent-encoded, exactly as they arrived.
 */
export interface DiscourseSsoReturn {
  sso: string;
  sig: string;
}

/**
 * The span of a query string that IS a community sign-in: the base64 payload naming the
 * account, then the HMAC over it.
 *
 * The single copy, read by both `parseSsoReturn` and `stripSsoReturnFromSearch`, so the
 * two cannot disagree about what "the pair" is. That matters in one direction in
 * particular: a strip matching LESS than the parser read would leave a credential sitting
 * in the address bar for Mixpanel to film (#5151).
 *
 * `sig` must follow `sso` immediately, and either may sit after `?` or `&`. Do not swap it
 * for `URLSearchParams` — that reader turns a literal `+` into a space and the payload is
 * base64. The reasoning is in design-docs/discourse-sso.md, "The return leg on a phone".
 */
const SSO_RETURN_PATTERN = /[?&]sso=([A-Za-z0-9\+\/=%]+)&sig=([A-Fa-f0-9]+)/;

/**
 * Read the community sign-in pair out of a query string, or answer undefined when
 * it is not there. Both halves are required — either alone signs nobody in.
 *
 * The values come back EXACTLY as they appeared in the address, still
 * percent-encoded. Decoding happens once, later, inside
 * `SsoService.completeExchangeAsync`, because decoding can fail on a corrupted or
 * crafted link and only there is somebody able to report it and tell the person
 * their sign-in did not work. Decoding here would either throw into a promise
 * nobody awaits or be swallowed, and both make a broken link vanish silently.
 *
 * NEVER log the returned values or the address carrying them: together they ARE a
 * forum login for that person (#4525).
 *
 * The pattern it reads is `SSO_RETURN_PATTERN` above, which says what the shape is and
 * why it is a regular expression rather than `URLSearchParams`.
 */
export function parseSsoReturn(search: string): DiscourseSsoReturn | undefined {
  const [, sso, sig] = search.match(SSO_RETURN_PATTERN) || [null, null, null];
  if (!sso || !sig) {
    return undefined;
  }
  return { sso, sig };
}

/**
 * The query string with the community sign-in pair taken out of it.
 *
 * This is how the pair stops being visible to things that read the address on their
 * own. Mixpanel's session replay films the page and its heatmap events carry
 * `$current_url`, and neither goes through any code of ours, so the only way to keep
 * the credential out of them is for the address to stop holding it (#5151).
 *
 * Works on the raw text for the same reason `parseSsoReturn` does: `URLSearchParams`
 * reads a literal `+` as a space, and re-serialising the parameters we are KEEPING
 * through it would corrupt them exactly that way. String surgery leaves every byte we
 * are not removing alone.
 */
export function stripSsoReturnFromSearch(search: string): string {
  const stripped = search.replace(SSO_RETURN_PATTERN, '');
  // the pattern swallows the separator in front of the pair, so a pair that sat FIRST
  // takes the leading `?` with it and what is left starts with `&`. Put the `?` back
  // rather than leaving an address the browser would read as one parameter named `&foo`.
  if (stripped.startsWith('&')) {
    return '?' + stripped.substring(1);
  }
  return stripped;
}

/**
 * The page this app was loaded on, as the SSO landing decision needs to see it.
 *
 * An interface rather than `window` itself so the decision below is testable without a
 * browser — the same reason this whole module holds no Angular.
 */
export interface SsoLandingPage {
  /** The full address, `location.href`. */
  href: string;
  /** The host this page was served from, `location.host`. */
  host: string;
  /** The query string, `location.search`. */
  search: string;
  /** The root domain the return-host cookie is scoped to. */
  rootDomain: string;
  /** Send the browser to another address, ending this page (`location.replace`). */
  bounceTo(url: string): void;
  /** Change what the address bar says WITHOUT navigating (`history.replaceState`). */
  rewriteAddress(url: string): void;
}

/**
 * What `handleSsoLanding` did, and what the caller should do next.
 *
 * `bounced` and `not-an-sso-return` both mean "there is no sign-in for you to run
 * here", but they are separate answers because only one of them has already started a
 * navigation — a caller that treated them alike could try to draw a page onto a
 * document that is on its way out.
 */
export type SsoLandingOutcome =
  | { kind: 'not-an-sso-return' }
  | { kind: 'bounced' }
  | { kind: 'taken'; sso: string; sig: string };

/**
 * Decide what this page load is, take the sign-in pair if there is one, and leave the
 * address bar in the right state.
 *
 * The order matters and is the whole reason this is one function rather than steps at
 * the call site:
 *
 * 1. If the address carries no pair, nothing here applies and nothing is touched.
 * 2. The multi-environment bounce (#3300) comes next. Discourse always returns people
 *    to the canonical `www` host, so a sign-in that STARTED on `dev` or `canary` has
 *    to be handed on to that host — WITH the pair, because the pair is the sign-in.
 *    That leg must therefore run before any clearing, and it ends this page.
 * 3. Only on the remaining path — this host is running the exchange itself — is the
 *    pair cleared out of the address bar. See `stripSsoReturnFromSearch` for why that
 *    clearing exists at all (#5151).
 *
 * The values handed back are still percent-encoded, exactly as `parseSsoReturn`
 * answers them. NEVER log them or the address carrying them: together they ARE a forum
 * login for that person (#4525).
 */
export function handleSsoLanding(page: SsoLandingPage): SsoLandingOutcome {
  const parsed = parseSsoReturn(page.search);
  if (!parsed) {
    return { kind: 'not-an-sso-return' };
  }

  // if this sign-in started on another one of our hosts, hand it back there
  const cookie = consumeReturnHostCookie(page.host, page.rootDomain);
  if (cookie.kind === 'mismatch') {
    const url = new URL(page.href);
    url.host = cookie.host;
    page.bounceTo(url.toString());
    return { kind: 'bounced' };
  }

  // this host runs the exchange, so the address has no further use for the pair
  const url = new URL(page.href);
  url.search = stripSsoReturnFromSearch(page.search);
  page.rewriteAddress(url.toString());
  return { kind: 'taken', sso: parsed.sso, sig: parsed.sig };
}
